import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash, PencilSimple, CaretDown, CaretRight, Broadcast, ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const emptyForm = {
  name: "",
  family: "v4",
  input_mode: "cidr",
  cidr: "",
  range_start: "",
  range_end: "",
  gateway: "",
  dns_primary: "",
  dns_secondary: "",
  notes: "",
  nas_ids: [],
};

export default function IPPools() {
  const [pools, setPools] = useState([]);
  const [nasList, setNasList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [expanded, setExpanded] = useState({});      // pool_id -> bool
  const [leases, setLeases] = useState({});          // pool_id -> array
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: p }, { data: n }] = await Promise.all([
        http.get("/ip-pools"),
        http.get("/nas"),
      ]);
      setPools(p || []);
      setNasList(n || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p.id);
    setForm({
      name: p.name || "",
      family: p.family || "v4",
      input_mode: p.input_mode || "cidr",
      cidr: p.cidr || "",
      range_start: p.range_start || "",
      range_end: p.range_end || "",
      gateway: p.gateway || "",
      dns_primary: p.dns_primary || "",
      dns_secondary: p.dns_secondary || "",
      notes: p.notes || "",
      nas_ids: p.nas_ids || [],
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { ...form };
      // Clear the fields that don't match input_mode so we don't send stale data
      if (body.input_mode === "cidr") { body.range_start = null; body.range_end = null; }
      else { body.cidr = null; }
      if (editing) await http.patch(`/ip-pools/${editing}`, body);
      else await http.post("/ip-pools", body);
      toast.success(editing ? "IP pool updated" : "IP pool created");
      setOpen(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this IP pool? Any plans linked to it will be unlinked.")) return;
    try {
      await http.delete(`/ip-pools/${id}`);
      toast.success("Pool deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const toggleLeases = async (id) => {
    if (expanded[id]) {
      setExpanded({ ...expanded, [id]: false });
      return;
    }
    setExpanded({ ...expanded, [id]: true });
    if (!leases[id]) {
      try {
        const { data } = await http.get(`/ip-pools/${id}/leases`);
        setLeases((l) => ({ ...l, [id]: data.leases || [] }));
      } catch (err) {
        toast.error("Failed to load leases");
      }
    }
  };

  const refreshLeases = async (id) => {
    setLeases((l) => ({ ...l, [id]: null }));
    try {
      const { data } = await http.get(`/ip-pools/${id}/leases`);
      setLeases((l) => ({ ...l, [id]: data.leases || [] }));
      load();
    } catch { /* noop */ }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="ip-pools-view">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="uppercase-label">Address management</div>
          <h1 className="font-display font-bold text-4xl mt-1">IP Pools</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Define IPv4 & IPv6 pools once, link them to plans, and let RADIUS push <span className="font-mono text-primary">Framed-Pool</span> to the NAS on every login. Utilisation is computed from static assignments + active RADIUS sessions. <span className="text-amber-400/90">Note: for <span className="font-mono">Framed-Pool</span> to actually assign IPs, a pool with the same name must also exist on your MikroTik/BNG (<span className="font-mono">/ip pool</span>). Otherwise leave <span className="font-mono">Address pool</span> blank on the plan and the router will pick from its own default.</span>
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-pool-btn" onClick={openNew} className="rounded-sm bg-primary hover:bg-primary/90"><Plus size={14} weight="bold" className="mr-2" /> New pool</Button>
          </DialogTrigger>
          <PoolDialog form={form} setForm={setForm} editing={editing} save={save} saving={saving} nasList={nasList} />
        </Dialog>
      </div>

      {loading && <div className="text-xs text-muted-foreground font-mono" data-testid="pools-loading">loading pools…</div>}
      {!loading && pools.length === 0 && (
        <div className="border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="pools-empty">
          No IP pools yet. Create one to start handing out addresses via RADIUS.
        </div>
      )}

      <div className="grid grid-cols-1 gap-3">
        {pools.map((p) => (
          <PoolCard
            key={p.id}
            pool={p}
            expanded={!!expanded[p.id]}
            leases={leases[p.id]}
            onToggle={() => toggleLeases(p.id)}
            onEdit={() => openEdit(p)}
            onDelete={() => remove(p.id)}
            onRefresh={() => refreshLeases(p.id)}
          />
        ))}
      </div>
    </div>
  );
}

function PoolCard({ pool, expanded, leases, onToggle, onEdit, onDelete, onRefresh }) {
  const total = pool.total || 0;
  const used = pool.used || 0;
  const pct = total > 0 ? Math.min(100, (used / total) * 100) : 0;
  const barColor = pct > 90 ? "bg-destructive" : pct > 70 ? "bg-amber-500" : "bg-primary";
  return (
    <div className="border border-border bg-card" data-testid={`pool-card-${pool.id}`}>
      <div className="p-4 sm:p-5 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-start">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={onToggle} className="text-muted-foreground hover:text-foreground" data-testid={`pool-expand-${pool.id}`}>
              {expanded ? <CaretDown size={14} /> : <CaretRight size={14} />}
            </button>
            <span className="font-display font-bold text-lg truncate">{pool.name}</span>
            <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 border ${pool.family === "v6" ? "border-cyan-500/50 text-cyan-400" : "border-primary/50 text-primary"}`}>
              IP{pool.family}
            </span>
          </div>
          <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-xs font-mono">
            <Stat k="Range" v={`${pool.range_start || "—"} → ${pool.range_end || "—"}`} span={2} />
            <Stat k="CIDR" v={pool.cidr || "—"} />
            <Stat k="Gateway" v={pool.gateway || "—"} />
            <Stat k="DNS" v={[pool.dns_primary, pool.dns_secondary].filter(Boolean).join(", ") || "—"} span={2} />
            <Stat k="Total" v={total.toLocaleString()} />
            <Stat k="Free" v={(pool.free ?? Math.max(total - used, 0)).toLocaleString()} />
          </div>
          <div className="mt-3">
            <div className="flex justify-between text-[10px] uppercase-label mb-1">
              <span>Utilisation</span>
              <span className="font-mono" data-testid={`pool-util-${pool.id}`}>{used.toLocaleString()} / {total.toLocaleString()} · {pct.toFixed(1)}%</span>
            </div>
            <div className="h-1.5 bg-secondary border border-border overflow-hidden">
              <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
            </div>
          </div>
          {pool.notes && <p className="text-xs text-muted-foreground mt-3">{pool.notes}</p>}
        </div>
        <div className="flex md:flex-col items-end gap-2">
          <Button variant="outline" size="sm" onClick={onEdit} data-testid={`pool-edit-${pool.id}`} className="rounded-sm text-xs h-8"><PencilSimple size={12} className="mr-1" /> Edit</Button>
          <Button variant="outline" size="sm" onClick={onDelete} data-testid={`pool-delete-${pool.id}`} className="rounded-sm text-xs h-8 border-destructive/40 text-destructive hover:bg-destructive/10"><Trash size={12} className="mr-1" /> Delete</Button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-border p-4 sm:p-5 bg-secondary/30" data-testid={`pool-leases-${pool.id}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="uppercase-label flex items-center gap-2"><Broadcast size={12} /> Active leases</div>
            <button onClick={onRefresh} className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1 font-mono" data-testid={`pool-refresh-${pool.id}`}>
              <ArrowClockwise size={12} /> refresh
            </button>
          </div>
          {leases === undefined || leases === null ? (
            <div className="text-xs text-muted-foreground font-mono">loading…</div>
          ) : leases.length === 0 ? (
            <div className="text-xs text-muted-foreground font-mono">No addresses currently leased from this pool.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left uppercase-label border-b border-border">
                    <th className="py-2">IP</th>
                    <th>Subscriber</th>
                    <th>Username</th>
                    <th>Source</th>
                    <th>Status</th>
                    <th>Since</th>
                  </tr>
                </thead>
                <tbody className="font-mono">
                  {leases.map((l, i) => (
                    <tr key={`${l.ip}-${i}`} className="border-b border-border/50">
                      <td className="py-2">{l.ip}</td>
                      <td>{l.full_name || "—"}</td>
                      <td>{l.username || "—"}</td>
                      <td className={l.source === "static" ? "text-cyan-400" : "text-primary"}>{l.source}</td>
                      <td>
                        <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${l.status === "online" ? "bg-primary" : "bg-muted-foreground"}`} />
                        {l.status || "offline"}
                      </td>
                      <td className="text-muted-foreground">{l.since ? new Date(l.since).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PoolDialog({ form, setForm, editing, save, saving, nasList = [] }) {
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleNas = (id) => setForm((f) => {
    const cur = f.nas_ids || [];
    return { ...f, nas_ids: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] };
  });
  const isRange = form.input_mode === "range";
  return (
    <DialogContent className="bg-card border-border max-w-2xl">
      <DialogHeader><DialogTitle className="font-display">{editing ? "Edit IP pool" : "Create IP pool"}</DialogTitle></DialogHeader>
      <form onSubmit={save} className="grid grid-cols-2 gap-4">
        <F label="Name" span={2}>
          <Input required data-testid="pool-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="pool-home-v4" />
        </F>

        <F label="Family">
          <div className="flex border border-border rounded-sm overflow-hidden">
            {["v4", "v6"].map((f) => (
              <button key={f} type="button" onClick={() => set("family", f)}
                data-testid={`pool-family-${f}`}
                className={`flex-1 py-2 text-xs font-mono uppercase ${form.family === f ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-accent/40"}`}>IP{f}</button>
            ))}
          </div>
        </F>

        <F label="Input mode">
          <div className="flex border border-border rounded-sm overflow-hidden">
            {[
              { k: "cidr", label: "CIDR" },
              { k: "range", label: "Start–End" },
            ].map((m) => (
              <button key={m.k} type="button" onClick={() => set("input_mode", m.k)}
                data-testid={`pool-mode-${m.k}`}
                className={`flex-1 py-2 text-xs font-mono uppercase ${form.input_mode === m.k ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-accent/40"}`}>{m.label}</button>
            ))}
          </div>
        </F>

        {!isRange && (
          <F label="CIDR" span={2}>
            <Input required data-testid="pool-cidr" value={form.cidr} onChange={(e) => set("cidr", e.target.value)}
              placeholder={form.family === "v6" ? "2001:db8:abcd::/48" : "10.10.0.0/22"} />
          </F>
        )}
        {isRange && (
          <>
            <F label="Start IP">
              <Input required data-testid="pool-start" value={form.range_start} onChange={(e) => set("range_start", e.target.value)}
                placeholder={form.family === "v6" ? "2001:db8::1" : "10.10.0.2"} />
            </F>
            <F label="End IP">
              <Input required data-testid="pool-end" value={form.range_end} onChange={(e) => set("range_end", e.target.value)}
                placeholder={form.family === "v6" ? "2001:db8::ffff" : "10.10.3.254"} />
            </F>
          </>
        )}

        <F label="Gateway"><Input data-testid="pool-gw" value={form.gateway} onChange={(e) => set("gateway", e.target.value)} placeholder="10.10.0.1" /></F>
        <F label="Primary DNS"><Input data-testid="pool-dns1" value={form.dns_primary} onChange={(e) => set("dns_primary", e.target.value)} placeholder="1.1.1.1" /></F>
        <F label="Secondary DNS" span={2}><Input data-testid="pool-dns2" value={form.dns_secondary} onChange={(e) => set("dns_secondary", e.target.value)} placeholder="8.8.8.8" /></F>
        <F label="Notes" span={2}><Textarea data-testid="pool-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="e.g. Home broadband dynamic pool, Bangalore" /></F>

        <F label="Allowed NAS (leave empty = all NAS)" span={2}>
          <div className="border border-border rounded-sm p-2 max-h-40 overflow-y-auto space-y-1" data-testid="pool-nas-list">
            {nasList.length === 0 && <div className="text-[11px] text-muted-foreground py-2 text-center">No NAS defined yet. This pool will be available to every NAS.</div>}
            {nasList.map((n) => {
              const checked = (form.nas_ids || []).includes(n.id);
              return (
                <label key={n.id} className={`flex items-center gap-2 px-2 py-1.5 text-xs rounded-sm cursor-pointer hover:bg-accent/40 ${checked ? "bg-primary/10" : ""}`}>
                  <input type="checkbox" checked={checked} onChange={() => toggleNas(n.id)} data-testid={`pool-nas-${n.id}`} />
                  <span className="font-mono flex-1">{n.name}</span>
                  <span className="font-mono text-muted-foreground">{n.nas_ip}</span>
                  <span className="uppercase text-[9px] text-muted-foreground">{n.vendor || "—"}</span>
                </label>
              );
            })}
          </div>
          <div className="text-[10px] text-muted-foreground mt-1">Pool appears in Subscribers → Services → Static Pool dropdown only when the subscriber's NAS is selected. Empty list = pool visible to every NAS.</div>
        </F>

        <DialogFooter className="col-span-2">
          <Button type="submit" disabled={saving} data-testid="pool-submit" className="rounded-sm bg-primary hover:bg-primary/90">
            {saving ? "Saving…" : editing ? "Update" : "Create"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

const F = ({ label, span = 1, children }) => (
  <div className={span === 2 ? "col-span-2" : ""}>
    <Label className="uppercase-label">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
const Stat = ({ k, v, span = 1 }) => (
  <div className={span === 2 ? "col-span-2" : ""}>
    <div className="uppercase-label text-[9px]">{k}</div>
    <div className="truncate">{v}</div>
  </div>
);
